/**
 * WorkspaceRegistry.ts
 * Canonical singleton registry and state authority for Wingman workspaces.
 * ZERO external dependencies. Reversible lifecycle.
 */

import React from 'react';
import { WingmanWorkspaceId, WingmanWorkspaceRegistration, WingmanWorkspaceMetadata } from './WorkspaceContract';

export type WorkspaceListener = (activeId: WingmanWorkspaceId | null) => void;

export class WorkspaceRegistryService {
  private static instance: WorkspaceRegistryService | null = null;
  private workspaces = new Map<WingmanWorkspaceId, WingmanWorkspaceRegistration>();
  private activeWorkspaceId: WingmanWorkspaceId | null = null;
  private listeners: Set<WorkspaceListener> = new Set();

  public static getInstance(): WorkspaceRegistryService {
    if (!WorkspaceRegistryService.instance) {
      WorkspaceRegistryService.instance = new WorkspaceRegistryService();
    }
    return WorkspaceRegistryService.instance;
  }

  public register(registration: WingmanWorkspaceRegistration): void {
    this.workspaces.set(registration.metadata.id, registration);
  }

  public get(id: WingmanWorkspaceId): WingmanWorkspaceRegistration | undefined {
    return this.workspaces.get(id);
  }

  public getAll(): WingmanWorkspaceMetadata[] {
    return Array.from(this.workspaces.values()).map(r => r.metadata);
  }

  public getActiveWorkspaceId(): WingmanWorkspaceId | null {
    return this.activeWorkspaceId;
  }

  public openWorkspace(id: WingmanWorkspaceId): boolean {
    if (!this.workspaces.has(id)) {
      console.warn(`[WorkspaceRegistry] Unknown workspace id: "${id}". Safe rejection.`);
      return false;
    }
    this.activeWorkspaceId = id;
    this.notify();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('WINGMAN_WORKSPACE_CHANGED', { detail: { activeId: id } }));
    }
    return true;
  }

  public closeWorkspace(): void {
    if (this.activeWorkspaceId === null) return;
    this.activeWorkspaceId = null;
    this.notify();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('WINGMAN_WORKSPACE_CHANGED', { detail: { activeId: null } }));
    }
  }

  public subscribe(listener: WorkspaceListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  public resetForTesting(): void {
    this.activeWorkspaceId = null;
    this.listeners.clear();
  }

  private notify(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.activeWorkspaceId);
      } catch (err) {
        console.error('[WorkspaceRegistry] Error notifying listener:', err);
      }
    });
  }
}

export const workspaceRegistry = WorkspaceRegistryService.getInstance();
export const openWorkspace = (id: WingmanWorkspaceId) => workspaceRegistry.openWorkspace(id);
export const closeWorkspace = () => workspaceRegistry.closeWorkspace();

if (typeof window !== 'undefined') {
  (window as any).WingmanWorkspace = {
    open: openWorkspace,
    close: closeWorkspace,
    registry: workspaceRegistry
  };
}
